import { useEffect, useRef, useState } from "react";

const DEFAULT_ENTER_EDGE_RATIO = 0.1;
const DEFAULT_EXIT_EDGE_RATIO = 0.08;

interface UseInViewOptions {
  /** Fracao do elemento visivel para disparar (0-1). */
  threshold?: number;
  /** Margem do viewport; bordas negativas criam uma zona curta de entrada/saida. */
  rootMargin?: string;
  /** Distancia da borda para entrar em animacoes reversiveis. */
  enterEdgeRatio?: number;
  /** Distancia da borda para sair em animacoes reversiveis. */
  exitEdgeRatio?: number;
  /** Dispara so uma vez e desconecta o observer (mais leve). */
  once?: boolean;
}

function getLayoutBounds(element: HTMLElement) {
  let top = 0;
  let current: HTMLElement | null = element;

  while (current) {
    top += current.offsetTop;
    current = current.offsetParent as HTMLElement | null;
  }

  return { top, bottom: top + element.offsetHeight };
}

function isInsideViewportBand(element: HTMLElement, edgeRatio: number) {
  const { top, bottom } = getLayoutBounds(element);
  const viewportTop = window.scrollY + window.innerHeight * edgeRatio;
  const viewportBottom = window.scrollY + window.innerHeight * (1 - edgeRatio);

  return bottom > viewportTop && top < viewportBottom;
}

/**
 * Equivalente "scroll-aware" ao ngAfterViewInit do Angular: observa quando o
 * elemento entra no viewport via IntersectionObserver. A animacao em si fica no
 * CSS (transform/opacity), que o navegador compoe na GPU.
 */
export function useInView<T extends HTMLElement = HTMLElement>({
  threshold = 0.01,
  rootMargin = "-7% 0px -7% 0px",
  enterEdgeRatio = DEFAULT_ENTER_EDGE_RATIO,
  exitEdgeRatio = DEFAULT_EXIT_EDGE_RATIO,
  once = true,
}: UseInViewOptions = {}) {
  const ref = useRef<T>(null);
  const inViewRef = useRef(false);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Acessibilidade + fallback: mostra direto, sem animar.
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    if (prefersReducedMotion || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }

    const updateInView = (nextInView: boolean) => {
      inViewRef.current = nextInView;
      setInView(nextInView);
    };

    if (!once) {
      let animationFrameId: number | null = null;

      const updateVisibility = () => {
        animationFrameId = null;
        const edgeRatio = inViewRef.current
          ? exitEdgeRatio
          : enterEdgeRatio;

        updateInView(isInsideViewportBand(el, edgeRatio));
      };

      const scheduleUpdate = () => {
        if (animationFrameId !== null) return;

        animationFrameId = window.requestAnimationFrame(updateVisibility);
      };

      scheduleUpdate();
      window.addEventListener("scroll", scheduleUpdate, { passive: true });
      window.addEventListener("resize", scheduleUpdate);

      return () => {
        if (animationFrameId !== null) {
          window.cancelAnimationFrame(animationFrameId);
        }

        window.removeEventListener("scroll", scheduleUpdate);
        window.removeEventListener("resize", scheduleUpdate);
      };
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          updateInView(true);
          observer.disconnect();
        }
      },
      { threshold, rootMargin },
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, [threshold, rootMargin, enterEdgeRatio, exitEdgeRatio, once]);

  return { ref, inView } as const;
}
